import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  listBloggers,
  getBloggerStats,
  assignShopsToBlogger,
  reassignBloggerShop,
  listContentTasks,
  patchContentTask,
  listBlogPosts,
  searchShopsForPicker,
  getAdminShop
} from '../../services/bloggerOpsAdminApi';

const TABS = [
  { id: 'bloggers', label: 'All bloggers' },
  { id: 'tasks', label: 'Content tasks' },
  { id: 'posts', label: 'Blog posts' }
];

const USER_STATUSES = [
  'PENDING',
  'ACCEPTED',
  'REJECTED',
  'HOLD',
  'SUSPENDED',
  'ACTIVE',
  'INACTIVE',
  'UNASSIGNED',
  'REQUESTED'
];

const CONTENT_TASK_STATUS = ['PENDING', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED', 'OVERDUE'];
const BLOG_POST_STATUS = ['DRAFT', 'SUBMITTED', 'REJECTED', 'LIVE'];
const PRIORITIES = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];

const NO_EXCLUDE_IDS = [];

function formatDt(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return String(iso);
  }
}

function errMsg(e) {
  return e?.response?.data?.message || e?.message || 'Request failed';
}

function normalizeDocId(id) {
  if (id == null || id === '') return '';
  if (typeof id === 'object') {
    if (id.$oid) return String(id.$oid);
    return String(id);
  }
  return String(id);
}

function bloggerFullName(b) {
  return b?.fullName || b?.user?.fullName || '—';
}

function bloggerPhone(b) {
  return b?.phone || b?.user?.phone || '';
}

function bloggerLabel(b) {
  if (!b) return '';
  const name = b.fullName || b.user?.fullName || 'Unknown';
  const zone = b.zone ? ` · ${b.zone}` : '';
  return `${b.employeeCode} — ${name}${zone}`;
}

function formatEnumLabel(status) {
  return String(status)
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function userStatusFor(blogger) {
  return blogger?.userStatus || blogger?.user?.status || '—';
}

function normalizePostImageUrl(entry) {
  if (!entry) return '';
  if (typeof entry === 'string') {
    const s = entry.trim();
    if (!s) return '';
    if (/^(https?:|data:|blob:)/i.test(s)) return s;
    const apiBase = process.env.REACT_APP_API_BASE_URL || '';
    const origin = apiBase.replace(/\/api\/?$/, '');
    if (s.startsWith('/')) return origin ? `${origin}${s}` : s;
    return s;
  }
  if (typeof entry === 'object') {
    return normalizePostImageUrl(entry.url || entry.imageUrl || entry.src || entry.uri || '');
  }
  return '';
}

function normalizePostImages(images) {
  if (!Array.isArray(images)) return [];
  return images.map(normalizePostImageUrl).filter(Boolean);
}

function PostImageGallery({ images }) {
  const urls = normalizePostImages(images);
  const [failed, setFailed] = useState(() => new Set());
  const [lightbox, setLightbox] = useState(null);

  if (urls.length === 0) return null;

  return (
    <>
      <div className={`grid gap-3 mb-4 ${urls.length > 1 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'}`}>
        {urls.map((url, i) => {
          const key = `${url}-${i}`;
          const broken = failed.has(key);
          return (
            <button
              key={key}
              type="button"
              className="block w-full rounded-lg border border-slate-200 bg-slate-50 overflow-hidden text-left focus:outline-none focus:ring-2 focus:ring-indigo-500"
              onClick={() => !broken && setLightbox({ url, index: i })}
              disabled={broken}
            >
              {broken ? (
                <div className="flex items-center justify-center min-h-[160px] px-4 text-sm text-slate-500">
                  Image could not be loaded
                </div>
              ) : (
                <img
                  src={url}
                  alt={`Post image ${i + 1}`}
                  className="w-full h-auto max-h-[min(70vh,480px)] object-contain mx-auto block"
                  loading="lazy"
                  onError={() => setFailed((prev) => new Set(prev).add(key))}
                />
              )}
            </button>
          );
        })}
      </div>

      {lightbox && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4"
          onClick={() => setLightbox(null)}
        >
          <div className="relative max-w-5xl max-h-[90vh] w-full" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="absolute -top-10 right-0 text-white text-sm hover:underline"
              onClick={() => setLightbox(null)}
            >
              Close
            </button>
            <img
              src={lightbox.url}
              alt={`Post image ${lightbox.index + 1}`}
              className="w-full h-auto max-h-[90vh] object-contain mx-auto rounded-lg"
            />
            <a
              href={lightbox.url}
              target="_blank"
              rel="noopener noreferrer"
              className="absolute -bottom-10 left-0 text-white text-sm hover:underline"
            >
              Open original
            </a>
          </div>
        </div>
      )}
    </>
  );
}

function useBloggerDirectory(showToast) {
  const [bloggers, setBloggers] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const merged = [];
      let page = 1;
      const limit = 100;
      let reportedTotal = 0;
      for (;;) {
        const { data } = await listBloggers({ page, limit });
        if (!data.success) {
          showToast(data.message || 'Could not load bloggers list', 'error');
          break;
        }
        const chunk = data.data || [];
        merged.push(...chunk);
        reportedTotal = data.total ?? merged.length;
        if (chunk.length < limit || merged.length >= reportedTotal) break;
        page += 1;
        if (page > 50) break;
      }
      setBloggers(merged);
    } catch (e) {
      showToast(errMsg(e), 'error');
      setBloggers([]);
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const byId = useMemo(() => {
    const m = new Map();
    bloggers.forEach((b) => m.set(normalizeDocId(b._id), b));
    return m;
  }, [bloggers]);

  return useMemo(
    () => ({ bloggers, loading, refresh, byId }),
    [bloggers, loading, refresh, byId]
  );
}

function BloggerSelect({
  label,
  value,
  onChange,
  bloggers,
  loading,
  excludeIds = NO_EXCLUDE_IDS,
  allowEmpty = true,
  emptyLabel = 'Any blogger',
  disabled = false,
  selectClassName = ''
}) {
  const exclude = useMemo(() => new Set(excludeIds.map(normalizeDocId)), [excludeIds]);
  const opts = bloggers.filter((b) => !exclude.has(normalizeDocId(b._id)));
  const selectCls = ['h-10 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white', selectClassName]
    .filter(Boolean)
    .join(' ');
  return (
    <label className={`block text-sm min-w-0 ${disabled ? 'opacity-60' : ''}`}>
      <span className="block text-slate-600 mb-1">{label}</span>
      <select
        className={selectCls}
        value={value}
        disabled={disabled || loading}
        onChange={(e) => onChange(e.target.value)}
        required={!allowEmpty}
      >
        {allowEmpty ? (
          <option value="">{loading ? 'Loading bloggers…' : emptyLabel}</option>
        ) : (
          <option value="" disabled={!loading}>
            {loading ? 'Loading bloggers…' : emptyLabel}
          </option>
        )}
        {opts.map((b) => {
          const vid = normalizeDocId(b._id);
          return (
            <option key={vid} value={vid}>
              {bloggerLabel(b)}
            </option>
          );
        })}
      </select>
    </label>
  );
}

function ShopPickerSingle({ label, value, onChange, showToast, allowClear = true }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [pickedLabel, setPickedLabel] = useState('');
  const [panelOpen, setPanelOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!value) {
      setPickedLabel('');
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await getAdminShop(value);
        const shop = data?.data ?? data;
        if (cancelled || !shop) return;
        const city = shop.address?.city || shop.city;
        setPickedLabel(`${shop.name || 'Shop'}${city ? ` — ${city}` : ''}`);
      } catch {
        if (!cancelled) setPickedLabel('');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [value]);

  useEffect(() => {
    if (value || !panelOpen) return undefined;
    const t = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const { data } = await searchShopsForPicker({
          search: query.trim(),
          page: 1,
          limit: 35
        });
        const list = data?.data;
        setResults(Array.isArray(list) ? list : []);
      } catch (e) {
        showToast(errMsg(e), 'error');
        setResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 320);
    return () => clearTimeout(t);
  }, [query, value, panelOpen, showToast]);

  useEffect(() => {
    if (!panelOpen || value) return undefined;
    const close = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setPanelOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [panelOpen, value]);

  return (
    <div className="relative text-sm min-w-0" ref={containerRef}>
      <span className="block text-slate-600 mb-1">{label}</span>
      {value ? (
        <div className="h-10 rounded-lg border border-slate-200 bg-slate-50 px-3 flex items-center gap-2">
          <span className="flex-1 truncate text-slate-800 text-sm">{pickedLabel || 'Selected shop'}</span>
          {allowClear && (
            <button
              type="button"
              className="text-indigo-600 text-xs font-medium shrink-0"
              onClick={() => {
                onChange('');
                setQuery('');
                setPanelOpen(false);
              }}
            >
              Change
            </button>
          )}
        </div>
      ) : (
        <>
          <input
            className="w-full h-10 border border-slate-300 rounded-lg px-3 text-sm"
            placeholder="Search shop name, city…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => setPanelOpen(true)}
          />
          {panelOpen && (
            <ul className="absolute z-[100] left-0 right-0 top-full mt-1 max-h-52 overflow-y-auto border border-slate-200 rounded-lg bg-white shadow-lg divide-y divide-slate-100">
              {searchLoading ? (
                <li className="px-3 py-2 text-slate-500 text-xs">Searching…</li>
              ) : results.length === 0 ? (
                <li className="px-3 py-2 text-slate-500 text-xs">Type to search or pick from results</li>
              ) : (
                results.map((s) => {
                  const city = s.address?.city || s.city;
                  const sid = normalizeDocId(s._id);
                  return (
                    <li key={sid}>
                      <button
                        type="button"
                        className="w-full text-left px-3 py-2 hover:bg-indigo-50 text-sm"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          onChange(sid);
                          setPickedLabel(`${s.name || 'Shop'}${city ? ` — ${city}` : ''}`);
                          setQuery('');
                          setPanelOpen(false);
                        }}
                      >
                        <span className="font-medium">{s.name || '—'}</span>
                        {city ? <span className="text-slate-500"> — {city}</span> : null}
                      </button>
                    </li>
                  );
                })
              )}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="rounded-lg border border-slate-200 p-3">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-xl font-semibold text-slate-900">{value ?? '—'}</div>
    </div>
  );
}

function StatusBreakdown({ title, data }) {
  const entries = Object.entries(data || {});
  if (entries.length === 0) {
    return (
      <div>
        <div className="font-medium text-slate-800 mb-2">{title}</div>
        <p className="text-slate-500 text-sm">No data.</p>
      </div>
    );
  }
  return (
    <div>
      <div className="font-medium text-slate-800 mb-2">{title}</div>
      <div className="flex flex-wrap gap-2">
        {entries.map(([k, v]) => (
          <span key={k} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
            {formatEnumLabel(k)}: {v}
          </span>
        ))}
      </div>
    </div>
  );
}

export default function BloggerOperations() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = TABS.some((t) => t.id === searchParams.get('tab')) ? searchParams.get('tab') : 'bloggers';
  const setTab = (id) => setSearchParams({ tab: id });

  const [toast, setToast] = useState(null);
  const showToast = useCallback((message, type = 'ok') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  }, []);

  const bloggerDir = useBloggerDirectory(showToast);

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6">
      {toast && (
        <div
          className={`fixed bottom-6 right-6 z-50 rounded-lg px-4 py-3 shadow-lg text-sm ${
            toast.type === 'error' ? 'bg-red-600 text-white' : 'bg-slate-800 text-white'
          }`}
        >
          {toast.message}
        </div>
      )}

      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-900">Blogger — Blogger ops</h1>
        <p className="text-slate-600 mt-1 text-sm">
          Assign shops, manage content tasks, and review blog posts.
        </p>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-3 mb-6">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              tab === t.id ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'bloggers' && <BloggersTab showToast={showToast} bloggerDir={bloggerDir} />}
      {tab === 'tasks' && <ContentTasksTab showToast={showToast} bloggerDir={bloggerDir} />}
      {tab === 'posts' && <BlogPostsTab showToast={showToast} bloggerDir={bloggerDir} />}
    </div>
  );
}

function BloggersTab({ showToast, bloggerDir }) {
  const [status, setStatus] = useState('');
  const [zone, setZone] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [total, setTotal] = useState(0);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [statsOpen, setStatsOpen] = useState(false);
  const [statsFor, setStatsFor] = useState(null);
  const [statsData, setStatsData] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const [assignOpen, setAssignOpen] = useState(false);
  const [assignBlogger, setAssignBlogger] = useState(null);
  const [shopSearch, setShopSearch] = useState('');
  const [shopResults, setShopResults] = useState([]);
  const [shopSearchLoading, setShopSearchLoading] = useState(false);
  const [selectedShopIds, setSelectedShopIds] = useState(() => new Set());
  const [assignSubmitting, setAssignSubmitting] = useState(false);

  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassignOldBlogger, setReassignOldBlogger] = useState(null);
  const [reassignShopId, setReassignShopId] = useState('');
  const [reassignNewBloggerId, setReassignNewBloggerId] = useState('');
  const [reassignSubmitting, setReassignSubmitting] = useState(false);

  const { bloggers: allBloggers, loading: bloggerListLoading } = bloggerDir;

  const reassignTargetChoices = useMemo(() => {
    if (!reassignOldBlogger) return [];
    const ex = normalizeDocId(reassignOldBlogger._id);
    return allBloggers.filter((b) => normalizeDocId(b._id) !== ex);
  }, [allBloggers, reassignOldBlogger]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await listBloggers({
        page,
        limit,
        ...(status && { status }),
        ...(zone.trim() && { zone: zone.trim() })
      });
      if (data.success) {
        setRows(data.data || []);
        setTotal(data.total || 0);
      } else showToast(data.message || 'Failed to load', 'error');
    } catch (e) {
      showToast(errMsg(e), 'error');
    } finally {
      setLoading(false);
    }
  }, [page, limit, status, zone, showToast]);

  useEffect(() => {
    load();
  }, [load]);

  const openStats = async (b) => {
    setStatsFor(b);
    setStatsOpen(true);
    setStatsData(null);
    setStatsLoading(true);
    try {
      const { data } = await getBloggerStats(b._id);
      if (data.success) setStatsData(data.data);
      else showToast(data.message || 'Failed to load stats', 'error');
    } catch (e) {
      showToast(errMsg(e), 'error');
    } finally {
      setStatsLoading(false);
    }
  };

  const searchShops = async () => {
    setShopSearchLoading(true);
    try {
      const { data } = await searchShopsForPicker({ search: shopSearch.trim(), page: 1, limit: 25 });
      const list = data?.data;
      setShopResults(Array.isArray(list) ? list : []);
    } catch (e) {
      showToast(errMsg(e), 'error');
      setShopResults([]);
    } finally {
      setShopSearchLoading(false);
    }
  };

  useEffect(() => {
    if (!assignOpen) return undefined;
    const t = setTimeout(() => searchShops(), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopSearch, assignOpen]);

  const toggleShop = (id) => {
    const nid = normalizeDocId(id);
    setSelectedShopIds((prev) => {
      const next = new Set(prev);
      if (next.has(nid)) next.delete(nid);
      else next.add(nid);
      return next;
    });
  };

  const submitAssign = async () => {
    if (!assignBlogger || !selectedShopIds.size) {
      showToast('Select at least one shop', 'error');
      return;
    }
    setAssignSubmitting(true);
    try {
      const { data } = await assignShopsToBlogger({
        bloggerId: assignBlogger._id,
        shopIds: Array.from(selectedShopIds)
      });
      if (data.success) {
        showToast(data.message || 'Shops assigned');
        setAssignOpen(false);
        setSelectedShopIds(new Set());
        load();
        bloggerDir.refresh();
      } else showToast(data.message || 'Failed', 'error');
    } catch (e) {
      showToast(errMsg(e), 'error');
    } finally {
      setAssignSubmitting(false);
    }
  };

  const submitReassign = async () => {
    if (!reassignOldBlogger || !reassignShopId.trim() || !reassignNewBloggerId.trim()) {
      showToast('Select shop and new blogger', 'error');
      return;
    }
    setReassignSubmitting(true);
    try {
      const { data } = await reassignBloggerShop({
        oldBloggerId: reassignOldBlogger._id,
        newBloggerId: reassignNewBloggerId.trim(),
        shopId: reassignShopId.trim()
      });
      if (data.success) {
        showToast(data.message || 'Reassigned');
        setReassignOpen(false);
        setReassignShopId('');
        setReassignNewBloggerId('');
        load();
        bloggerDir.refresh();
      } else showToast(data.message || 'Failed', 'error');
    } catch (e) {
      showToast(errMsg(e), 'error');
    } finally {
      setReassignSubmitting(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-4 items-end">
        <label className="text-sm">
          <span className="block text-slate-600 mb-1">Account status</span>
          <select
            className="border border-slate-300 rounded-lg px-3 py-2 text-sm"
            value={status}
            onChange={(e) => {
              setPage(1);
              setStatus(e.target.value);
            }}
          >
            <option value="">Any</option>
            {USER_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm flex-1 min-w-[140px]">
          <span className="block text-slate-600 mb-1">Zone contains</span>
          <input
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
            value={zone}
            onChange={(e) => {
              setPage(1);
              setZone(e.target.value);
            }}
            placeholder="e.g. North"
          />
        </label>
        <button type="button" onClick={load} className="px-4 py-2 bg-slate-800 text-white rounded-lg text-sm font-medium">
          Refresh
        </button>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-left">
            <tr>
              <th className="px-3 py-2 font-medium">Code</th>
              <th className="px-3 py-2 font-medium">User</th>
              <th className="px-3 py-2 font-medium">Zone</th>
              <th className="px-3 py-2 font-medium">User status</th>
              <th className="px-3 py-2 font-medium">Shops</th>
              <th className="px-3 py-2 font-medium">Posts / views</th>
              <th className="px-3 py-2 font-medium">Tasks</th>
              <th className="px-3 py-2 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={8} className="px-3 py-8 text-center text-slate-500">
                  Loading…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-3 py-8 text-center text-slate-500">
                  No bloggers found
                </td>
              </tr>
            ) : (
              rows.map((b) => (
                <tr key={b._id} className="border-t border-slate-100 hover:bg-slate-50/80">
                  <td className="px-3 py-2 font-mono text-xs">{b.employeeCode}</td>
                  <td className="px-3 py-2">
                    <div className="font-medium text-slate-900">{bloggerFullName(b)}</div>
                    <div className="text-xs text-slate-500">{bloggerPhone(b)}</div>
                  </td>
                  <td className="px-3 py-2">{b.zone || '—'}</td>
                  <td className="px-3 py-2">
                    <span className="inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium">
                      {userStatusFor(b)}
                    </span>
                  </td>
                  <td className="px-3 py-2">{b.assignedShopsCount ?? 0}</td>
                  <td className="px-3 py-2 text-xs">
                    {b.totalPosts ?? 0} live · {b.totalViews ?? 0} views
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-600 max-w-[180px]">
                    {b.taskStats
                      ? Object.entries(b.taskStats)
                          .map(([k, v]) => `${k}: ${v}`)
                          .join(', ')
                      : '—'}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap space-x-2">
                    <button type="button" className="text-indigo-600 hover:underline text-xs font-medium" onClick={() => openStats(b)}>
                      Stats
                    </button>
                    <button
                      type="button"
                      className="text-indigo-600 hover:underline text-xs font-medium"
                      onClick={() => {
                        setAssignBlogger(b);
                        setSelectedShopIds(new Set());
                        setShopSearch('');
                        setAssignOpen(true);
                      }}
                    >
                      Assign shops
                    </button>
                    <button
                      type="button"
                      className="text-indigo-600 hover:underline text-xs font-medium"
                      onClick={() => {
                        setReassignOldBlogger(b);
                        setReassignShopId('');
                        setReassignNewBloggerId('');
                        setReassignOpen(true);
                      }}
                    >
                      Reassign shop
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={page} totalPages={totalPages} total={total} onPageChange={setPage} />

      {statsOpen && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4" onClick={() => setStatsOpen(false)}>
          <div
            className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-semibold">Blogger dashboard</h3>
              <button type="button" className="text-slate-500 hover:text-slate-800" onClick={() => setStatsOpen(false)}>
                ✕
              </button>
            </div>
            {statsFor && (
              <p className="text-sm text-slate-600 mb-4">
                {statsFor.employeeCode} — {bloggerFullName(statsFor)}
              </p>
            )}
            {statsLoading ? (
              <p className="text-slate-500">Loading…</p>
            ) : statsData ? (
              <div className="space-y-5 text-sm">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <StatCard label="Shops onboarded" value={statsData.shopsOnboarded} />
                  <StatCard label="Live posts" value={statsData.totalPosts} />
                  <StatCard label="Total views" value={statsData.totalViews} />
                  <StatCard label="Total likes" value={statsData.totalLikes} />
                  <StatCard label="Posts submitted" value={statsData.contentMetrics?.postsSubmitted} />
                  <StatCard label="Tasks completed" value={statsData.contentMetrics?.tasksCompleted} />
                </div>
                <StatusBreakdown title="Tasks by status" data={statsData.tasksByStatus} />
                <StatusBreakdown title="Posts by status" data={statsData.postsByStatus} />
              </div>
            ) : (
              <p className="text-slate-500">No data</p>
            )}
          </div>
        </div>
      )}

      {assignOpen && assignBlogger && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4" onClick={() => setAssignOpen(false)}>
          <div
            className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold mb-1">Assign shops</h3>
            <p className="text-sm text-slate-600 mb-4">
              To <strong>{assignBlogger.employeeCode}</strong> — {bloggerFullName(assignBlogger)}
            </p>
            <label className="block text-sm mb-2">
              <span className="text-slate-600">Search shops</span>
              <input
                className="mt-1 w-full h-10 border border-slate-300 rounded-lg px-3 text-sm"
                value={shopSearch}
                onChange={(e) => setShopSearch(e.target.value)}
                placeholder="Name, city, phone…"
              />
            </label>
            <p className="text-xs text-slate-500 mb-2">{shopSearchLoading ? 'Searching…' : `${shopResults.length} results`}</p>
            <ul className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-56 overflow-y-auto mb-4 bg-white">
              {shopResults.map((s) => {
                const id = normalizeDocId(s._id);
                const checked = selectedShopIds.has(id);
                const city = s.address?.city || s.city;
                const state = s.address?.state || s.state;
                const locality = [city, state].filter(Boolean).join(', ');
                return (
                  <li key={id}>
                    <label className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-slate-50 text-left w-full">
                      <input
                        type="checkbox"
                        className="h-4 w-4 shrink-0 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                        checked={checked}
                        onChange={() => toggleShop(id)}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block font-medium text-slate-900 truncate">{s.name || '—'}</span>
                        {locality ? (
                          <span className="block text-xs text-slate-500 truncate">{locality}</span>
                        ) : null}
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
            <div className="flex gap-2 justify-end">
              <button type="button" className="px-4 py-2 rounded-lg border border-slate-300 text-sm" onClick={() => setAssignOpen(false)}>
                Cancel
              </button>
              <button
                type="button"
                disabled={assignSubmitting}
                onClick={submitAssign}
                className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium disabled:opacity-50"
              >
                {assignSubmitting ? 'Saving…' : 'Assign selected'}
              </button>
            </div>
          </div>
        </div>
      )}

      {reassignOpen && reassignOldBlogger && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4" onClick={() => setReassignOpen(false)}>
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-1">Reassign shop</h3>
            <p className="text-sm text-slate-600 mb-4">
              From <strong>{reassignOldBlogger.employeeCode}</strong> to another blogger
            </p>
            <div className="space-y-4">
              <ShopPickerSingle
                label="Shop to move"
                value={reassignShopId}
                onChange={setReassignShopId}
                showToast={showToast}
              />
              <BloggerSelect
                label="Assign to blogger"
                value={reassignNewBloggerId}
                onChange={setReassignNewBloggerId}
                bloggers={reassignTargetChoices}
                loading={bloggerListLoading}
                allowEmpty
                emptyLabel="Choose blogger…"
              />
              {!bloggerListLoading && reassignTargetChoices.length === 0 && (
                <p className="text-xs text-amber-900 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                  No other bloggers available. Approve another blogger before reassigning.
                </p>
              )}
            </div>
            <div className="flex gap-2 justify-end mt-6">
              <button type="button" className="px-4 py-2 rounded-lg border border-slate-300 text-sm" onClick={() => setReassignOpen(false)}>
                Cancel
              </button>
              <button
                type="button"
                disabled={reassignSubmitting}
                onClick={submitReassign}
                className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium disabled:opacity-50"
              >
                {reassignSubmitting ? 'Saving…' : 'Reassign'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ContentTasksTab({ showToast, bloggerDir }) {
  const [assignedTo, setAssignedTo] = useState('');
  const [status, setStatus] = useState('');
  const [priority, setPriority] = useState('');
  const [shopId, setShopId] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [total, setTotal] = useState(0);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const { bloggers, loading: bloggerLoading, byId } = bloggerDir;

  const [editTask, setEditTask] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await listContentTasks({
        page,
        limit,
        ...(assignedTo.trim() && { assignedTo: assignedTo.trim() }),
        ...(status && { status }),
        ...(priority && { priority }),
        ...(shopId.trim() && { shopId: shopId.trim() })
      });
      if (data.success) {
        setRows(data.data || []);
        setTotal(data.total || 0);
      } else showToast(data.message || 'Failed', 'error');
    } catch (e) {
      showToast(errMsg(e), 'error');
    } finally {
      setLoading(false);
    }
  }, [page, limit, assignedTo, status, priority, shopId, showToast]);

  useEffect(() => {
    load();
  }, [load]);

  const openEdit = (t) => {
    setEditTask(t);
    setEditForm({
      title: t.title,
      description: t.description || '',
      status: t.status,
      priority: t.priority,
      notes: t.notes || '',
      assignedTo: normalizeDocId(t.assignedTo),
      dueDate: t.dueDate ? new Date(t.dueDate).toISOString().slice(0, 16) : ''
    });
  };

  const submitEdit = async () => {
    if (!editTask) return;
    setEditSaving(true);
    try {
      const body = {
        title: editForm.title,
        description: editForm.description,
        status: editForm.status,
        priority: editForm.priority,
        notes: editForm.notes,
        assignedTo: editForm.assignedTo,
        dueDate: editForm.dueDate ? new Date(editForm.dueDate).toISOString() : null
      };
      const { data } = await patchContentTask(editTask._id, body);
      if (data.success) {
        showToast('Task updated');
        setEditTask(null);
        load();
      } else showToast(data.message || 'Failed', 'error');
    } catch (e) {
      showToast(errMsg(e), 'error');
    } finally {
      setEditSaving(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));
  const filterSelectCls =
    'h-10 w-full min-h-[2.5rem] border border-slate-300 rounded-lg px-3 text-sm bg-white';

  return (
    <div>
      <p className="text-sm text-slate-600 mb-4">
        Content tasks are created by shop owners. Admins can edit, cancel, or reassign tasks.
      </p>
      <div className="flex flex-wrap gap-2 mb-4">
        <button type="button" onClick={load} className="px-4 py-2 border border-slate-300 rounded-lg text-sm">
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <BloggerSelect
          label="Assigned blogger"
          value={assignedTo}
          onChange={(v) => {
            setPage(1);
            setAssignedTo(v);
          }}
          bloggers={bloggers}
          loading={bloggerLoading}
          emptyLabel="All assignees"
        />
        <div>
          <span className="block text-slate-600 mb-1 text-sm">Status</span>
          <select
            className={filterSelectCls}
            value={status}
            onChange={(e) => {
              setPage(1);
              setStatus(e.target.value);
            }}
          >
            <option value="">Any status</option>
            {CONTENT_TASK_STATUS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        <div>
          <span className="block text-slate-600 mb-1 text-sm">Priority</span>
          <select
            className={filterSelectCls}
            value={priority}
            onChange={(e) => {
              setPage(1);
              setPriority(e.target.value);
            }}
          >
            <option value="">Any priority</option>
            {PRIORITIES.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </div>
        <div>
          <span className="block text-slate-600 mb-1 text-sm">Shop ID</span>
          <input
            className={filterSelectCls}
            value={shopId}
            onChange={(e) => {
              setPage(1);
              setShopId(e.target.value);
            }}
            placeholder="Filter by shop ObjectId"
          />
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-left">
            <tr>
              <th className="px-3 py-2 font-medium">Code</th>
              <th className="px-3 py-2 font-medium">Title</th>
              <th className="px-3 py-2 font-medium">Blogger</th>
              <th className="px-3 py-2 font-medium">Status</th>
              <th className="px-3 py-2 font-medium">Priority</th>
              <th className="px-3 py-2 font-medium">Due</th>
              <th className="px-3 py-2 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="px-3 py-8 text-center text-slate-500">
                  Loading…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-3 py-8 text-center text-slate-500">
                  No content tasks
                </td>
              </tr>
            ) : (
              rows.map((t) => (
                <tr key={t._id} className="border-t border-slate-100 hover:bg-slate-50/80">
                  <td className="px-3 py-2 font-mono text-xs">{t.taskCode}</td>
                  <td className="px-3 py-2 max-w-[200px] truncate" title={t.title}>
                    {t.title}
                  </td>
                  <td className="px-3 py-2 text-xs max-w-[140px] truncate">
                    {byId.get(normalizeDocId(t.assignedTo))
                      ? bloggerLabel(byId.get(normalizeDocId(t.assignedTo)))
                      : `…${String(normalizeDocId(t.assignedTo)).slice(-6)}`}
                  </td>
                  <td className="px-3 py-2">
                    <span className="inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-xs">{t.status}</span>
                  </td>
                  <td className="px-3 py-2 text-xs">{t.priority}</td>
                  <td className="px-3 py-2 text-xs">{formatDt(t.dueDate)}</td>
                  <td className="px-3 py-2">
                    <button type="button" className="text-indigo-600 hover:underline text-xs font-medium" onClick={() => openEdit(t)}>
                      Edit
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={page} totalPages={totalPages} total={total} onPageChange={setPage} />

      {editTask && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4" onClick={() => setEditTask(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-1">Edit content task</h3>
            <p className="text-xs font-mono text-slate-500 mb-4">{editTask.taskCode}</p>
            <div className="space-y-3 text-sm">
              <label className="block">
                <span className="text-slate-600">Title</span>
                <input
                  className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2"
                  value={editForm.title}
                  onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))}
                />
              </label>
              <label className="block">
                <span className="text-slate-600">Description</span>
                <textarea
                  className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2"
                  rows={2}
                  value={editForm.description}
                  onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
                />
              </label>
              <label className="block">
                <span className="text-slate-600">Status</span>
                <select
                  className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2"
                  value={editForm.status}
                  onChange={(e) => setEditForm((f) => ({ ...f, status: e.target.value }))}
                >
                  {CONTENT_TASK_STATUS.filter((s) => s !== 'OVERDUE').map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="text-slate-600">Priority</span>
                <select
                  className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2"
                  value={editForm.priority}
                  onChange={(e) => setEditForm((f) => ({ ...f, priority: e.target.value }))}
                >
                  {PRIORITIES.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              </label>
              <BloggerSelect
                label="Assigned blogger"
                value={editForm.assignedTo}
                onChange={(v) => setEditForm((f) => ({ ...f, assignedTo: v }))}
                bloggers={bloggers}
                loading={bloggerLoading}
                allowEmpty={false}
                emptyLabel="Choose blogger…"
              />
              <label className="block">
                <span className="text-slate-600">Due date</span>
                <input
                  type="datetime-local"
                  className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2"
                  value={editForm.dueDate}
                  onChange={(e) => setEditForm((f) => ({ ...f, dueDate: e.target.value }))}
                />
              </label>
              <label className="block">
                <span className="text-slate-600">Notes</span>
                <textarea
                  className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2"
                  rows={2}
                  value={editForm.notes}
                  onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))}
                />
              </label>
            </div>
            <div className="flex gap-2 justify-end mt-6">
              <button type="button" className="px-4 py-2 rounded-lg border border-slate-300 text-sm" onClick={() => setEditTask(null)}>
                Cancel
              </button>
              <button
                type="button"
                disabled={editSaving}
                onClick={submitEdit}
                className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium disabled:opacity-50"
              >
                {editSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function BlogPostsTab({ showToast, bloggerDir }) {
  const [bloggerId, setBloggerId] = useState('');
  const [shopId, setShopId] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [total, setTotal] = useState(0);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewPost, setViewPost] = useState(null);

  const { bloggers, loading: bloggerLoading, byId } = bloggerDir;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await listBlogPosts({
        page,
        limit,
        ...(bloggerId.trim() && { bloggerId: bloggerId.trim() }),
        ...(shopId.trim() && { shopId: shopId.trim() }),
        ...(status && { status })
      });
      if (data.success) {
        setRows(data.data || []);
        setTotal(data.total || 0);
      } else showToast(data.message || 'Failed', 'error');
    } catch (e) {
      showToast(errMsg(e), 'error');
    } finally {
      setLoading(false);
    }
  }, [page, limit, bloggerId, shopId, status, showToast]);

  useEffect(() => {
    load();
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / limit));
  const filterSelectCls =
    'h-10 w-full min-h-[2.5rem] border border-slate-300 rounded-lg px-3 text-sm bg-white';

  return (
    <div>
      <p className="text-sm text-slate-600 mb-4">
        Read-only view of blog posts. Approve / reject is handled by shop owners.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <BloggerSelect
          label="Blogger"
          value={bloggerId}
          onChange={(v) => {
            setPage(1);
            setBloggerId(v);
          }}
          bloggers={bloggers}
          loading={bloggerLoading}
          emptyLabel="All bloggers"
        />
        <div>
          <span className="block text-slate-600 mb-1 text-sm">Status</span>
          <select
            className={filterSelectCls}
            value={status}
            onChange={(e) => {
              setPage(1);
              setStatus(e.target.value);
            }}
          >
            <option value="">Any status</option>
            {BLOG_POST_STATUS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        <div>
          <span className="block text-slate-600 mb-1 text-sm">Shop ID</span>
          <input
            className={filterSelectCls}
            value={shopId}
            onChange={(e) => {
              setPage(1);
              setShopId(e.target.value);
            }}
            placeholder="Filter by shop ObjectId"
          />
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-left">
            <tr>
              <th className="px-3 py-2 font-medium">Code</th>
              <th className="px-3 py-2 font-medium">Title</th>
              <th className="px-3 py-2 font-medium">Blogger</th>
              <th className="px-3 py-2 font-medium">Status</th>
              <th className="px-3 py-2 font-medium">Views / likes</th>
              <th className="px-3 py-2 font-medium">Updated</th>
              <th className="px-3 py-2 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="px-3 py-8 text-center text-slate-500">
                  Loading…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-3 py-8 text-center text-slate-500">
                  No blog posts
                </td>
              </tr>
            ) : (
              rows.map((p) => (
                <tr key={p._id} className="border-t border-slate-100 hover:bg-slate-50/80">
                  <td className="px-3 py-2 font-mono text-xs">{p.postCode}</td>
                  <td className="px-3 py-2 max-w-[200px] truncate" title={p.title}>
                    {p.title}
                  </td>
                  <td className="px-3 py-2 text-xs max-w-[140px] truncate">
                    {byId.get(normalizeDocId(p.bloggerId))
                      ? bloggerLabel(byId.get(normalizeDocId(p.bloggerId)))
                      : `…${String(normalizeDocId(p.bloggerId)).slice(-6)}`}
                  </td>
                  <td className="px-3 py-2">
                    <span className="inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-xs">{p.status}</span>
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {p.viewCount ?? 0} / {p.likeCount ?? 0}
                  </td>
                  <td className="px-3 py-2 text-xs">{formatDt(p.updatedAt)}</td>
                  <td className="px-3 py-2">
                    <button type="button" className="text-indigo-600 hover:underline text-xs font-medium" onClick={() => setViewPost(p)}>
                      View
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={page} totalPages={totalPages} total={total} onPageChange={setPage} />

      {viewPost && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4" onClick={() => setViewPost(null)}>
          <div
            className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-semibold">{viewPost.title}</h3>
                <p className="text-xs font-mono text-slate-500 mt-1">{viewPost.postCode}</p>
              </div>
              <button type="button" className="text-slate-500 hover:text-slate-800" onClick={() => setViewPost(null)}>
                ✕
              </button>
            </div>
            <div className="flex flex-wrap gap-2 mb-4 text-xs">
              <span className="rounded-full bg-slate-100 px-2 py-0.5 font-medium">{viewPost.status}</span>
              {viewPost.publishedAt && (
                <span className="text-slate-500">Published {formatDt(viewPost.publishedAt)}</span>
              )}
            </div>
            {viewPost.status === 'REJECTED' && viewPost.reviewFeedback && (
              <div className="mb-4 rounded-lg bg-red-50 border border-red-100 px-3 py-2 text-sm text-red-800">
                <strong>Review feedback:</strong> {viewPost.reviewFeedback}
              </div>
            )}
            <div className="prose prose-sm max-w-none text-slate-800 whitespace-pre-wrap mb-4">{viewPost.body || '—'}</div>
            <PostImageGallery images={viewPost.images} />
            <div className="grid grid-cols-2 gap-3 text-sm text-slate-600 border-t border-slate-100 pt-4">
              <div>Views: {viewPost.viewCount ?? 0}</div>
              <div>Likes: {viewPost.likeCount ?? 0}</div>
              <div>Created: {formatDt(viewPost.createdAt)}</div>
              <div>Updated: {formatDt(viewPost.updatedAt)}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Pagination({ page, totalPages, total, onPageChange }) {
  return (
    <div className="flex justify-between items-center mt-4 text-sm text-slate-600">
      <span>
        Page {page} of {totalPages} ({total} total)
      </span>
      <div className="space-x-2">
        <button
          type="button"
          disabled={page <= 1}
          onClick={() => onPageChange((p) => Math.max(1, p - 1))}
          className="px-3 py-1 rounded border border-slate-300 disabled:opacity-40"
        >
          Previous
        </button>
        <button
          type="button"
          disabled={page >= totalPages}
          onClick={() => onPageChange((p) => p + 1)}
          className="px-3 py-1 rounded border border-slate-300 disabled:opacity-40"
        >
          Next
        </button>
      </div>
    </div>
  );
}
